import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminBillingList } from "../../hooks/useAdminBillingList";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { SubscriptionDetailModal } from "../../components/admin/billing/SubscriptionDetailModal";
import { formatCentsUsd, mrrBreakdown, mrrCents, subscriptionStatusTone, type SubscriptionSummary } from "../../types/billing";
import { formatDateTimeCompact } from "../../utils/formatDateTime";
import styles from "./AdminBilling.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

const HEADERS = ["Customer", "Tenant", "Status", "Items", "MRR", "Created"];

// Blended monthly-equivalent, with the real cadence beneath it whenever the
// subscription has annual lines. `?? 0` guards rows seeded from a localStorage
// cache written before yearlyTotalCents existed.
const MrrCell = ({ summary }: { readonly summary: SubscriptionSummary }): JSX.Element => {
  const totals = { monthlyCents: summary.monthlyTotalCents, yearlyCents: summary.yearlyTotalCents ?? 0 };
  const breakdown = mrrBreakdown(totals);
  return (
    <td className={styles.num}>
      {formatCentsUsd(mrrCents(totals))}
      {breakdown !== null && <div className={styles.mrrBreakdown}>{breakdown}</div>}
    </td>
  );
};

// Fleet-wide billing tab: every Stripe subscription, searchable by customer
// email, opening a detail modal that reuses the per-tenant SubscriptionDetail.
export const AdminBilling = (): JSX.Element => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const { status, subscriptions, error, isFetching, refresh } = useAdminBillingList({ email: debounced });
  const [selected, setSelected] = useState<SubscriptionSummary | null>(null);

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Subscriptions">
        Every Stripe subscription. Open a row to manage its slots, entitlement and billing portal.
      </AdminPageIntro>

      <section className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search customer email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </section>

      <section className={styles.listSection}>
        {status === "loading" && <AdminTableSkeleton headers={HEADERS} tableClassName={styles.table ?? ""} />}
        {status === "error" && (
          <div className={styles.errorPanel}>Couldn&apos;t load billing{error !== null ? `: ${error}` : "."}</div>
        )}
        {status === "ready" && subscriptions.length === 0 && (
          <div className={styles.empty}>
            {debounced.trim().length > 0 ? "No subscriptions match that search." : "No subscriptions yet."}
          </div>
        )}
        {status === "ready" && subscriptions.length > 0 && (
          <table className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Tenant</th>
                <th>Status</th>
                <th className={styles.num}>Items</th>
                <th className={styles.num}>MRR</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((s) => (
                <tr
                  key={s.stripeSubscriptionId}
                  className={styles.row}
                  tabIndex={0}
                  onClick={() => setSelected(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSelected(s);
                  }}
                >
                  <td>
                    <span className={styles.clip} title={s.customerEmail ?? s.stripeCustomerId}>
                      {s.customerEmail ?? <span className={styles.muted}>{s.stripeCustomerId}</span>}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {s.tenantId !== null ? (
                      <Link to={`../instances/${s.tenantId}`} className={`${styles.tenantLink} ${styles.clip}`} title={s.tenantId}>
                        {s.tenantId}
                      </Link>
                    ) : (
                      <span className={styles.muted}>unlinked</span>
                    )}
                  </td>
                  <td>
                    <span className={styles[`status_${subscriptionStatusTone(s.status)}`] ?? ""}>{s.status}</span>
                  </td>
                  <td className={styles.num}>{s.activeItemCount}</td>
                  <MrrCell summary={s} />
                  <td>{s.createdAt !== null ? formatDateTimeCompact(s.createdAt) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected !== null && (
        <SubscriptionDetailModal
          summary={selected}
          onClose={() => {
            setSelected(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};
